import { Document } from '@contentful/rich-text-types'

import type { THeadlineMetricDto } from '@homepal/api-contract'

import {
  Condition,
  FilterType,
  RollingType,
  UnitPrefix
} from 'types/GlobalKpiOption'
import { IPermission } from 'types/GlobalPermission'
import { TSelectedCategory } from 'types/GlobalCategory'
import { PeriodFilter, SegmentPeriod, WidgetType } from './GlobalWidget'
import { TSlicesResult } from 'types/GlobalSlice'
import type { IEntity } from 'types/GlobalEntities'

// --- Metric enums ---

export enum MetricType {
  STANDARD = 'STANDARD',
  CUSTOM = 'CUSTOM'
}

export enum MetricCalculationBlockType {
  'BLOCK' = 'block',
  'VARIABLE' = 'variable',
  'TEMPLATE' = 'template',
  'NUMBER' = 'number'
}

export enum MetricCalculationOperation {
  PLUS = '+',
  MINUS = '-',
  DIVISION = '/',
  MULTIPLICATION = '*'
}

// --- Metric calculation blocks ---

export type TMetricCalculationBlock = {
  id: string
  left_value: string | null
  right_value: string | null
  left_kpi_template_id: string | null
  right_kpi_template_id: string | null
  left_kpi_variable_id: string | null
  right_kpi_variable_id: string | null
  left_kpi_calculation_block: TMetricCalculationBlock | null
  right_kpi_calculation_block: TMetricCalculationBlock | null
  left_type: MetricCalculationBlockType | null
  right_type: MetricCalculationBlockType | null
  operation: MetricCalculationOperation
}

export type TPutMetricCalculationBlock = {
  left_kpi_calculation_block: TPutMetricCalculationBlock | null
  right_kpi_calculation_block: TPutMetricCalculationBlock | null
  left_kpi_template_id: string | null
  right_kpi_template_id: string | null
  left_kpi_variable_id: string | null
  right_kpi_variable_id: string | null
  left_value: string | null
  right_value: string | null
  operation: MetricCalculationOperation
}

// --- Misc ---

export enum ShowPerEnum {
  RENTABLE_SQM = 'RENTABLE_SQM',
  ATEMP = 'ATEMP',
  BTA = 'BTA',
  NUMBER_OF_OBJECTS = 'NUMBER_OF_OBJECTS',
  NUMBER_OF_DWELLINGS = 'NUMBER_OF_DWELLINGS',
  NUMBER_OF_SPACES = 'NUMBER_OF_SPACES',
  NUMBER_OF_UNITS = 'NUMBER_OF_UNITS',
  RENTED_SQM = 'RENTED_SQM',
  NUMBER_OF_DAYS = 'NUMBER_OF_DAYS',
  NUMBER_OF_WORKDAYS = 'NUMBER_OF_WORKDAYS',
  NUMBER_OF_MOVE_INS = 'NUMBER_OF_MOVE_INS',
  NUMBER_OF_SNOW_DAYS = 'NUMBER_OF_SNOW_DAYS'
}

export enum MetricStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MISSING_DATA = 'MISSING_DATA',
  MISSING_SLICES = 'MISSING_SLICES',
  MISSING_DEFINITION = 'MISSING_DEFINITION'
}

export type TMetricFilterType =
  | FilterType.INPUT_VALUE
  | FilterType.ONLY_LEFT
  | FilterType.NUMBER

export type TMetricCondition =
  | Condition.EQ
  | Condition.NE
  | Condition.IS_NOT_NULL
  | Condition.IS_NULL
  | Condition.GT
  | Condition.LT

export interface IMetricFilter {
  attribute_option_id: string
  condition: TMetricCondition
  type: TMetricFilterType
  values: string[]
}

export interface IMetricConfig {
  attribute: string
  condition: Condition
  description: string
  id: string
  incomplete: boolean
  metrics: string[]
  title: string
  unit: string
  attribute_values: {
    id: string
    name: string
    value: number | null
  }[]
}

// these are settings that are shared between
// standard metric and variant for custome economy
export type TMetricVariantConfig = {
  default_settings: {
    period_filter: PeriodFilter
  }
  distributions: {
    enum: ShowPerEnum
    filter_options: string[]
  }[]
  entity_id: string | null
  fact_table_id: string
  rolling_sums: RollingType[]
  unit: string | null
  unit_prefixes: {
    value: UnitPrefix
    label: string
  }[]
}

/**
 * A slice a derived metric's definition is built over. `configured` says
 * whether the customer has given it any filter conditions — an unconfigured
 * one makes the metric uncomputable.
 */
export type TDefinitionSlice = {
  configured: boolean
  entity_id: string
  id: string
  name: string
}

interface IMetricBase {
  access: {
    permissions: IPermission[]
    private: boolean
  }
  allow_delete: boolean
  allow_share: boolean
  category: TSelectedCategory
  config: IMetricConfig | null
  filters: IMetricFilter[]
  id: string
  increase_is_positive: boolean
  definition_slices: TDefinitionSlice[]
  status: MetricStatus
  subcategory: TSelectedCategory
  title: { sv: string; en: string }
  type: MetricType
  unit: string | null // may be overwritten from variant
  url: string
  validated_at: string | null
}

export interface IStandardMetric extends IMetricBase, TMetricVariantConfig {
  type: MetricType.STANDARD
  body: { sv: Document; en: Document }
  calculation: { sv: Document; en: Document }
  summary: { sv: string; en: string }
}

export type TCustomEconomyMetricVariant = TMetricVariantConfig & {
  full_title: { sv: string; en: string }
  id: string
  subcategory: TSelectedCategory
  title: { sv: string; en: string }
}

export interface ICustomEconomyMetric extends IMetricBase {
  custom_calculation_block: TMetricCalculationBlock | null
  default_variant: string
  full_title: { sv: string; en: string }
  homepal: boolean
  type: MetricType.CUSTOM
  variants: TCustomEconomyMetricVariant[]
}

export type IMetric = IStandardMetric | ICustomEconomyMetric

export type ISelectMetric = IMetric &
  TMetricVariantConfig & {
    entity: IEntity | null
    slices: TSlicesResult
  }

export type TMetricWithConfig = IMetric & {
  config: IMetricConfig
}

export type TMetricSettings = {
  rolling_sum: RollingType | null
  distribution: ShowPerEnum | null
  slices: string[]
  variant_kpi_template_id: string | null
}

export type TCustomizedMetric = {
  id: string
  settings: TMetricSettings
}

/**
 * The metric the results page leads with, as the backend resolves it for
 * this customer (GET /v1/kpis/headline). Structurally a TCustomizedMetric,
 * so it goes straight into the metric components.
 */
export type THeadlineMetric = THeadlineMetricDto<TMetricSettings>

// A metric resolved for display + fetching: its identity and settings plus the
// fully-resolved query constraints (period, filters, visible columns). Used by
// the underlying-data table, the charts, and the chat — anywhere we describe or
// fetch exactly what the user is currently looking at.
export type TMetricView = TMetricSettings & {
  metric_id: string
  period_filter: PeriodFilter | null
  from_date: string | null
  to_date: string | null
  filters: Record<string, string[]>
  // The columns the user currently has visible, so fetched data matches.
  // Undefined = the metric's default columns.
  entity_columns?: string[]

  row_count?: number
}

export type TMetricQueryParams = {
  filter?: {
    [key: string]: {
      eq: string[]
    }
  }
  from_date?: string
  // REFERENCE-LINES
  line_1?: number
  line_1_label?: string
  line_2?: number
  line_2_label?: string
  period_filter?: PeriodFilter
  real_estates?: string[]
  segment_by?: string | null
  to_date?: string
  widget_type?: WidgetType
}

// Points a metric-data fetch at a stored kpi option so the backend computes
// on its real widget — applying the widget's and option's stored filter
// conditions — instead of building a dummy widget from settings alone.
export type TMetricSourceRef = {
  type: 'kpi_option'
  id: string
}

export type TMetricDataPostBody = TMetricSettings & {
  query_params?: TMetricQueryParams
  source?: TMetricSourceRef
  // false skips the server-side key_figure computation (an extra query),
  // for callers that only consume the segmented rows. Defaults to true.
  include_key_figure?: boolean
}

// One data point in a metric series. Shared by the metrics and key-metrics
// data endpoints — both produce rows from the same repository, so the shape
// is identical: `period` is a range object for time-series segments and a
// plain string (or null) otherwise.
export type TMetricDataRow = {
  period: SegmentPeriod | string | null
  segment: string | null
  value: number | null
}

export type IMetricRawData = {
  id: string
  data: TMetricDataRow[]
  key_figure?: { value: number | null }
  segment_attribute_option_id: string | null
}

// Response from the tokenless GET /v1/public/metrics/:id — metadata plus the
// series computed on the demo account, so the public preview page needs a
// single call and no redux stores.
export type TPublicMetric = IMetricRawData & {
  increase_is_positive: boolean
  is_time_series: boolean
  title: { sv: string; en: string }
  unit: string | null
}

export type TMetricsResult = {
  all: IMetric[]
  active: IMetric[]
  inactive: IMetric[]
  missing_data: IMetric[]
}

export type TMetricFilterOptions = {
  [attributeOptionId: string]: {
    id: string
    name: string
    numeric: boolean // numeric input instead of a value list
    options: string[]
  }
}

// typeguards
export const isStandardMetric = (m?: IMetric | null): m is IStandardMetric =>
  m != null && m.type === MetricType.STANDARD

export const isCustomEconomyMetric = (
  m?: IMetric | null
): m is ICustomEconomyMetric => m != null && m.type === MetricType.CUSTOM

export const isPercentageMetric = (
  m?: Pick<IMetricBase, 'unit'> | null
): boolean => m?.unit === '%'
